import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import useUploadFile from '@/hooks/useUploadFile'
import { cn } from '@/lib/utils'
import {
    Upload,
    Image as ImageIcon,
    Copy,
    Check,
    Trash2,
    ChevronDown,
    ChevronUp,
} from 'lucide-react'
import { useRef, useState, DragEvent, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'

interface UploadedImage {
    name: string
    url: string
    markdownUrl: string
}

const STORAGE_URL = import.meta.env.APP_STORAGE || ''

const ReleaseNotesImageUploader = () => {
    const { t } = useTranslation('release-notes')
    const [isExpanded, setIsExpanded] = useState(false)
    const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([])
    const [copiedUrl, setCopiedUrl] = useState<string | null>(null)
    const [isDragActive, setIsDragActive] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const { uploadAsync, isUploading, percent } = useUploadFile({
        prefix: 'release-notes',
        isPublic: true,
        onSuccess: (results) => {
            results.forEach((result) => {
                if (
                    result.status === 'fulfilled' &&
                    result.value.attachment_file_url
                ) {
                    const fileUrl = result.value.attachment_file_url
                    const fullUrl = `${STORAGE_URL}/${fileUrl}`
                    const newImage: UploadedImage = {
                        name: result.value.attachment_file_name || 'image',
                        url: fullUrl,
                        markdownUrl: `![${result.value.attachment_file_name}](${fullUrl})`,
                    }
                    setUploadedImages((prev) => [...prev, newImage])
                }
            })
            toast.success(
                t('image-upload.upload-success', 'Upload thành công!')
            )
        },
    })

    const handleFiles = async (files: FileList | null) => {
        if (!files || files.length === 0) return
        const imageFiles = Array.from(files).filter((f) =>
            f.type.startsWith('image/')
        )
        if (imageFiles.length > 0) {
            await uploadAsync(imageFiles)
        }
    }

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragActive(false)
        handleFiles(e.dataTransfer.files)
    }

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragActive(true)
    }

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragActive(false)
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files)
        if (inputRef.current) inputRef.current.value = ''
    }

    const copyToClipboard = async (text: string) => {
        await navigator.clipboard.writeText(text)
        setCopiedUrl(text)
        toast.success(t('image-upload.copied'))
        setTimeout(() => setCopiedUrl(null), 2000)
    }

    const removeImage = (index: number) => {
        setUploadedImages((prev) => prev.filter((_, i) => i !== index))
    }

    return (
        <Card className="mb-6">
            <CardHeader
                className="cursor-pointer py-3"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" />
                        {t('image-upload.title')}
                    </CardTitle>
                    {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                    ) : (
                        <ChevronDown className="h-4 w-4" />
                    )}
                </div>
            </CardHeader>

            {isExpanded && (
                <CardContent className="pt-0">
                    {/* Dropzone */}
                    <div
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => inputRef.current?.click()}
                        className={cn(
                            'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                            isDragActive
                                ? 'border-primary bg-primary/5'
                                : 'border-muted-foreground/25 hover:border-primary/50'
                        )}
                    >
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={handleInputChange}
                        />
                        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        {isUploading ? (
                            <p className="text-sm text-muted-foreground">
                                {t('image-upload.uploading')}{' '}
                                {Object.values(percent)[0] || 0}%
                            </p>
                        ) : isDragActive ? (
                            <p className="text-sm text-primary">
                                {t('image-upload.drop-here')}
                            </p>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                {t('image-upload.drag-or-click')}
                            </p>
                        )}
                    </div>

                    {/* Uploaded Images List */}
                    {uploadedImages.length > 0 && (
                        <div className="mt-4 space-y-3">
                            <h4 className="text-sm font-medium">
                                {t('image-upload.uploaded')} (
                                {uploadedImages.length})
                            </h4>
                            {uploadedImages.map((image, index) => (
                                <div
                                    key={index}
                                    className="border rounded-lg p-3 space-y-2"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium truncate flex-1">
                                            {image.name}
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-destructive"
                                            onClick={() => removeImage(index)}
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </Button>
                                    </div>

                                    {/* URL Input */}
                                    <div className="flex gap-2">
                                        <Input
                                            value={image.url}
                                            readOnly
                                            className="text-xs h-8"
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 px-2"
                                            onClick={() =>
                                                copyToClipboard(image.url)
                                            }
                                        >
                                            {copiedUrl === image.url ? (
                                                <Check className="h-3 w-3" />
                                            ) : (
                                                <Copy className="h-3 w-3" />
                                            )}
                                        </Button>
                                    </div>

                                    {/* Markdown Input */}
                                    <div className="flex gap-2">
                                        <Input
                                            value={image.markdownUrl}
                                            readOnly
                                            className="text-xs h-8 font-mono"
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 px-2"
                                            onClick={() =>
                                                copyToClipboard(
                                                    image.markdownUrl
                                                )
                                            }
                                        >
                                            {copiedUrl === image.markdownUrl ? (
                                                <Check className="h-3 w-3" />
                                            ) : (
                                                <Copy className="h-3 w-3" />
                                            )}
                                        </Button>
                                    </div>

                                    {/* Preview */}
                                    <img
                                        src={image.url}
                                        alt={image.name}
                                        className="max-h-32 rounded border"
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            )}
        </Card>
    )
}

export default ReleaseNotesImageUploader
